import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";
import { formatAmount } from "../billing/format";

const dur = (k: "Seconds" | "Minutes" | "Hours") =>
  `dashboard.home.profile.detail.duration${k}` as MessageKey;

/** Number / percent / money / duration / day formatting for the owner overview (Latin digits in AR + EN). */
export function useOverviewFormat() {
  const { t, locale } = useI18n();
  const lang = locale === "ar" ? "ar" : "en";
  const intl = lang === "ar" ? "ar-u-nu-latn" : "en";
  const nf = new Intl.NumberFormat(intl, { maximumFractionDigits: 1 });
  const days = new Intl.DateTimeFormat(intl, { month: "short", day: "numeric", timeZone: "UTC" });
  const time = new Intl.DateTimeFormat(intl, { timeStyle: "short" });
  return {
    t,
    locale: lang,
    num: (n: number) => nf.format(n),
    percent: (n: number) => `${nf.format(n)}%`,
    money: (minor: string, currency: string) => formatAmount(minor, currency, lang),
    /** Seconds in the same words the home cards use (sec / min / h), like `ProfileHome`. */
    duration: (seconds: number): string =>
      seconds < 60
        ? t(dur("Seconds"), { count: nf.format(Math.max(1, Math.round(seconds))) })
        : seconds < 3600
          ? t(dur("Minutes"), { count: nf.format(Math.round(seconds / 60)) })
          : t(dur("Hours"), { count: nf.format(Math.round(seconds / 360) / 10) }),
    /** `YYYY-MM-DD` (workspace-local day) as a short label; the raw text if it does not parse. */
    day: (date: string): string => {
      const d = new Date(`${date}T00:00:00Z`);
      return Number.isNaN(d.getTime()) ? date : days.format(d);
    },
    time: (iso: string): string => {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "" : time.format(d);
    },
  };
}

export type OverviewFormat = ReturnType<typeof useOverviewFormat>;
